import { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Container,
  Dialog,
  Grid,
  Paper,
  Stack,
  Typography,
  IconButton,
  Box,
  TextField as MuiTextField
} from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { FormProvider, Table, TextField, SelectTag, Checkbox } from '../../../../../components';
import { dropdownFetch, request, toast } from '../../../../../utils';
import { PAYMENT_MODE, SPONSOR_TYPE } from '../../../../../constants';
import { useLocation } from 'react-router-dom';
import { CustomButtonn } from '../../../../../components';
import CloseIcon from '@mui/icons-material/Close';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';

const initialTemplateDataObj = {
  data: [],
  loading: false
};

const TEMPLATE_HEADER = [
  {
    header: 'Test Name',
    accessor: (row) => {
      const status = row.status;
      return (
        <span
          style={{
            color: status === 'inactive' ? 'red' : 'black'
          }}
        >
          {`${row.test_name?.test_name}`}
        </span>
      );
    },
    sx: { textTransform: 'capitalize' }
  },
  {
    header: 'Sub Group',
    accessor: (row) => row?.sub_group?.sub_group,
    sx: { textTransform: 'capitalize' }
  },
  {
    header: 'Main Group',
    accessor: (row) => row?.main_group?.main_group,
    sx: { textTransform: 'capitalize' }
  },
  {
    header: 'Status',
    accessor: 'status',
    sx: { textTransform: 'capitalize' }
  }
];
export const LisTemplatesMaster = () => {
  const [loading, setLoading] = useState(false);
  const [templateMasterDataObj, setTemplateMasterDataObj] = useState(initialTemplateDataObj);
  const [currentAction, setCurrentAction] = useState();
  const [selectedRecord, setSelectedRecord] = useState();
  const { pathname } = useLocation();
  const lastPathSegment = pathname.split('/').pop();
  const [mainGroupDropdown, setMainGroupDropdown] = useState([]);
  const [subGroupDrpdown, setSubGroupDropdown] = useState([]);
  const [testDropdown, setTestDropdown] = useState([]);
  const [openQueryDialog, setOpenQueryDialog] = useState(false);
  const [input, setInput] = useState('');
  const [editorData, setEditorData] = useState('');

  const handleEditorChange = (event, editor) => {
    const data = editor.getData();
    setEditorData(data);
    console.log({ data });
  };

  const methods = useForm({
    resolver: yupResolver(
      Yup.object().shape({
        test_name: Yup.string().required('Test Name  Is Required '),
        sub_group: Yup.string().required('Sub Group  Is Required '),
        main_group: Yup.string().required('Sub Group  Is Required ')
      })
    ),
    mode: 'all'
  });
  const { handleSubmit, setValue, reset } = methods;

  useEffect(() => {
    if (selectedRecord) {
      setValue('test_name', selectedRecord.test_name);
      setValue('sub_group', selectedRecord.sub_group);
      setValue('main_group', selectedRecord.main_group);
      setValue('description', selectedRecord.description);
      setEditorData(selectedRecord?.editorContent);
    } else {
      reset();
    }
  }, [setValue, reset, selectedRecord]);

  const formSubmit = async (formValues) => {
    setLoading(true);
    let editFlag = false;
    if (selectedRecord && currentAction === 'edit') {
      editFlag = true;
    }
    const response = await request(editFlag ? '/lis-template-update' : '/lis-template-create', {
      method: editFlag ? 'PUT' : 'POST',
      params: editFlag ? selectedRecord._id : null,
      body: {
        editorContent: editorData,
        test_name: formValues.test_name,
        sub_group: formValues.sub_group,
        main_group: formValues.main_group,
        description: formValues.description
      }
    });
    if (response?.success) {
      toast(`Successfully ${editFlag ? 'updated' : 'created'} Template master`, { variant: 'success' });
      setSelectedRecord(response.data);
      setCurrentAction();
      reset();
    } else {
      toast(`Failed to ${editFlag ? 'update' : 'create'} Template master`, {
        variant: 'error'
      });
    }
    setLoading(false);
  };

  const handleQueryWindow = async () => {
    setOpenQueryDialog(true);
    setTemplateMasterDataObj({ loading: true });
    const response = await request('/lis-template-list', { method: 'GET' });
    if (response.success) {
      const filteredData = response?.data?.filter(
        (item) => item.status === 'active' || item.status === 'inactive' || item.status === 'default'
      );
      setTemplateMasterDataObj({ loading: false, data: filteredData });
    } else {
      toast('Error in fetching data', { variant: 'error' });
      setTemplateMasterDataObj({ loading: false });
    }
  };

  const handleCancelRecord = async () => {
    setLoading(true);
    const response = await request('/lis-template-cancel', {
      method: 'DELETE',
      params: selectedRecord._id
    });
    if (response?.success) {
      toast('Successfully cancel Template master', { variant: 'success' });
      setSelectedRecord();
    } else {
      toast('Failed to cancel Template master', { variant: 'error' });
    }
    setLoading(false);
  };

  const handleDeleteRecord = async () => {
    setLoading(true);
    const response = await request('/lis-template-delete', {
      method: 'DELETE',
      params: selectedRecord._id
    });
    if (response?.success) {
      toast('Successfully deleted Template master', { variant: 'success' });
      setSelectedRecord();
    } else {
      toast('Failed to delete Template master', { variant: 'error' });
    }
    setLoading(false);
  };

  const getMainGroupDropDown = useCallback(async () => {
    try {
      const data = await dropdownFetch('test-main-group');
      setMainGroupDropdown(data);
    } catch (error) {
      console.error('Error fetching main group dropdown:', error);
      setMainGroupDropdown([]);
    }
  }, []);

  const getSubGroupDropDown = useCallback(async () => {
    try {
      const data = await dropdownFetch('test-sub-group');
      setSubGroupDropdown(data);
    } catch (error) {
      console.error('Error fetching Sub Group dropdown:', error);
      setSubGroupDropdown([]);
    }
  }, []);

  const getTestDropDown = useCallback(async () => {
    try {
      const data = await dropdownFetch('lis-test');
      setTestDropdown(data);
    } catch (error) {
      console.error('Error fetching Test dropdown:', error);
      setTestDropdown([]);
    }
  }, []);

  useEffect(() => {
    getMainGroupDropDown();
    getSubGroupDropDown();
    getTestDropDown();
  }, [getMainGroupDropDown, getSubGroupDropDown, getTestDropDown]);

  useEffect(() => {
    handleTemplateSearch(input);
  }, [input]);

  const handleTemplateSearch = async () => {
    const lowercasedInput = input.toLowerCase().trim();
  
      try {
        const response = await request('/lis-template-list', {
          method: 'GET',
          query: { description: lowercasedInput }
        });
        if (response.success && response.data) {
          setTemplateMasterDataObj({ loading: false, data: response.data });
        } else {
          toast('Error in fetching data', { variant: 'error' });
          setTemplateMasterDataObj({ loading: false, data: [] });
        }
      } catch (error) {
        console.error('Error during search:', error);
        toast('Error during search', { variant: 'error' });
        setTemplateMasterDataObj({ loading: false, data: [] });
      }
 
  };

  const handleChange = (e) => {
    const newInput = e.target.value;
    setInput(newInput);
  };

  return (
    <>
      <Paper sx={{ p: 0, flex: 1, borderRadius: 0, boxShadow: 'none' }}>
        <Container
          sx={{
            mt: 1,
            border: '0px solid',
            borderColor: 'grey.300',
            borderRadius: 1,
            width: '100%',
            maxWidth: 'none',
            minWidth: '100%',
            boxSizing: 'border-box'
          }}
        >
          <FormProvider methods={methods} onSubmit={handleSubmit(formSubmit)}>
            <Stack
              direction="row"
              spacing={2}
              justifyContent="space-between"
              alignItems="center"
              sx={{
                mb: 1,
                borderBottom: '0px solid',
                borderColor: 'grey.300'
              }}
            >
              <Typography color="primary" variant="h3" sx={{ fontWeight: 700 }}>
                Templates Master
              </Typography>
              <Stack direction="row" justifyContent="flex-end" sx={{ mb: 3 }} spacing={1}>
                <CustomButtonn
                  id={`${lastPathSegment}Write`}
                  variant="contained"
                  disabled={['edit', 'new'].includes(currentAction)}
                  onClick={() => {
                    setSelectedRecord();
                    setCurrentAction('new');
                  }}
                >
                  New
                </CustomButtonn>
                <CustomButtonn
                  id={`${lastPathSegment}Update`}
                  variant="contained"
                  disabled={['new', 'edit'].includes(currentAction) || !selectedRecord}
                  onClick={() => setCurrentAction('edit')}
                >
                  Edit
                </CustomButtonn>
                <CustomButtonn
                  id={'Submit'}
                  type="submit"
                  variant="contained"
                  disabled={!['edit', 'new'].includes(currentAction) || loading}
                  onClick={handleSubmit(formSubmit)}
                >
                  Save
                </CustomButtonn>
                <CustomButtonn
                  id={'Cancel'}
                  variant="contained"
                  disabled={['edit', 'new'].includes(currentAction) || loading}
                  onClick={() => {
                    setCurrentAction();
                    handleCancelRecord();
                  }}
                >
                  Cancel
                </CustomButtonn>
                <CustomButtonn
                  id={`isSuperAdmin`}
                  variant="contained"
                  disabled={!selectedRecord || ['new', 'edit'].includes(currentAction) || loading}
                  onClick={handleDeleteRecord}
                >
                  Delete
                </CustomButtonn>
                <CustomButtonn
                  id={`${lastPathSegment}Read`}
                  variant="contained"
                  disabled={['new', 'edit'].includes(currentAction)}
                  onClick={handleQueryWindow}
                >
                  Query
                </CustomButtonn>
                <CustomButtonn
                  id={`${lastPathSegment}Print`}
                  variant="contained"
                  disabled={!selectedRecord || currentAction}
                >
                  Print
                </CustomButtonn>
                <CustomButtonn
                  id={'Exit'}
                  variant="contained"
                  onClick={() => {
                    setCurrentAction('');
                  }}
                >
                  Exit
                </CustomButtonn>
              </Stack>
            </Stack>
            <Box
              sx={{
                border: '0px solid gray',
                padding: 2,
                borderRadius: '8px',
                width: '80%',
                boxShadow: '0px 2px 10px #0099ff'
              }}
            >
              <Grid container spacing={1}>
                <Grid item xs={6} sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>Test Name</Typography>
                    <SelectTag
                      disabled={!['new', 'edit'].includes(currentAction)}
                      formControlSX={{ flex: 0.8 }}
                      name="test_name"
                      menus={testDropdown.map((data) => ({ ...data, name: data.test_name }))}
                    />
                  </Stack>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>Main Group</Typography>
                    <SelectTag
                      disabled={!['new', 'edit'].includes(currentAction)}
                      formControlSX={{ flex: 0.8 }}
                      name="main_group"
                      menus={mainGroupDropdown.map((data) => ({ ...data, name: data.main_group }))}
                    />
                  </Stack>
                </Grid>
                <Grid item xs={6} sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>Description</Typography>
                    <TextField
                      disabled={!['new', 'edit'].includes(currentAction)}
                      sx={{ flex: 0.8 }}
                      name="description"
                      placeholder="Enter Description"
                    />
                  </Stack>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.2, fontWeight: 700, fontSize: '1.4rem' }}>Sub Group</Typography>
                    <SelectTag
                      disabled={!['new', 'edit'].includes(currentAction)}
                      formControlSX={{ flex: 0.8 }}
                      name="sub_group"
                      menus={subGroupDrpdown.map((data) => ({ ...data, name: data.sub_group }))}
                    />
                  </Stack>
                </Grid>
                <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <CKEditor
                    editor={ClassicEditor}
                    data={editorData}
                    onReady={(editor) => {
                      console.log('Editor is ready to use!', editor);
                    }}
                    onChange={handleEditorChange}
                    onBlur={(event, editor) => {
                      console.log('Blur.', editor);
                    }}
                    onFocus={(event, editor) => {
                      console.log('Focus.', editor);
                    }}
                  />
                </Grid>
              </Grid>
            </Box>
          </FormProvider>
        </Container>
      </Paper>
      <Dialog
        open={openQueryDialog}
        onClose={() => setOpenQueryDialog(false)}
        PaperProps={{ sx: { width: '80%', p: 2 } }}
        maxWidth="xl"
        scroll="body"
      >
        <Stack
          padding={0.2}
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
          borderBottom="1px solid"
          borderColor="grey.300"
        >
          <Typography variant="h4" fontWeight="bold">
            Lis Template Master Query Window
          </Typography>
          <IconButton onClick={() => setOpenQueryDialog(false)}>
            <CloseIcon color="error" fontSize="large" />
          </IconButton>
        </Stack>
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="center" sx={{ mt: 0 }}>
          <MuiTextField
            sx={{
              width: '100%',
              '& .MuiOutlinedInput-root': {
                padding: '0',
                fontSize: '14px'
              },
              '& .MuiOutlinedInput-input': {
                padding: '8px 14px'
              },
              '& .MuiInputLabel-root': {
                fontSize: '1.2rem' // Adjust label font size
              }
            }}
            label="Search Templates Master"
            variant="outlined"
            value={input}
            onChange={handleChange}
            InputProps={{
              style: {
                fontSize: '12px',
                fontWeight: 600
              }
            }}
          />
        </Stack>
        <Table
          sx={{ mt: 1 }}
          columns={TEMPLATE_HEADER}
          data={templateMasterDataObj.data || []}
          loading={templateMasterDataObj.loading}
          onRowClick={(row) => {
            const { main_group, sub_group, test_name, ...otherDetails } = row;
            setSelectedRecord({
              ...otherDetails,
              main_group: main_group?._id,
              sub_group: sub_group?._id,
              test_name: test_name?._id
            });
            setOpenQueryDialog(false);
          }}
        />
      </Dialog>
    </>
  );
};
